import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef } from "react";
import { useEscEntry, type EscLayers } from "../../../lib/encounter/escLayers";
import type { CombatWindowSpec, useCombatWindows } from "../../../lib/encounter/windows";
import { WindowFrame } from "./WindowFrame";

/**
 * A combat window's layer-5 Esc entry (`decisions/0189`), registered while `active`: Esc closes the
 * window focused most recently. Coming to the front is the only thing that raises a window's z (a
 * close below it lowers it; an open above it leaves it), so a rising z raises the entry within its
 * layer — a mousedown, a keyboard focus inside it, or `windows.open` on it while already open.
 *
 * Layer 5 also holds members outside the window registry (the Map key), whose z never moves. So the
 * returned focus handler raises the entry directly as well: focusing the frontmost window still puts
 * it above a Map key opened after it.
 */
export function useCombatWindowEsc(args: {
  esc: EscLayers;
  windows: ReturnType<typeof useCombatWindows>;
  id: string;
  active: boolean;
  onDismiss: () => void;
}): () => void {
  const { esc, windows, id, active, onDismiss } = args;
  useEscEntry(esc, { layer: "window", id, active, onDismiss });
  const z = windows.zOf(id);
  const zRef = useRef(z);
  useEffect(() => {
    if (active && z > zRef.current) esc.raise(id);
    zRef.current = z;
  }, [active, z, esc, id]);
  return () => {
    windows.focus(id);
    esc.raise(id);
  };
}

/**
 * One combat window, wired to the per-mode registry AND the Esc layers. It renders its
 * {@link WindowFrame} only when open in the active mode, and holds a layer-5 Esc entry while open
 * (`decisions/0189`): Esc closes the most recently focused window. It unregisters on close or
 * unmount (a mode flip parks it), and raises within layer 5 whenever it comes to the front — a
 * mousedown, a keyboard focus inside it, or `windows.open` on it while already open.
 */
export function CombatWindow({
  windows,
  esc,
  spec,
  glyph,
  title,
  controls,
  children,
}: {
  windows: ReturnType<typeof useCombatWindows>;
  esc: EscLayers;
  spec: CombatWindowSpec;
  glyph: IconDefinition;
  title: string;
  /** Optional titlebar controls (rendered before the close button) — e.g. the NPC round counter. */
  controls?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { close } = windows;
  const open = windows.isOpen(spec.id);
  const focus = useCombatWindowEsc({ esc, windows, id: spec.id, active: open, onDismiss: () => close(spec.id) });

  const g = windows.geometryOf(spec.id);
  if (!open || !g) return null;
  return (
    <WindowFrame
      glyph={glyph}
      title={title}
      controls={controls}
      pos={g.pos}
      {...windows.windowProps(spec.id)}
      z={windows.zOf(spec.id)}
      onFocus={focus}
      onFocusIn={focus}
      onClose={() => windows.close(spec.id)}
      closeKey="Esc"
    >
      {children}
    </WindowFrame>
  );
}
